// Job Setup Page - Postcode entry for engineers
import { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { MapPin, Search, Loader2, ClipboardList } from 'lucide-react'
import { EngineerLayout } from '../../components/layout/EngineerLayout'
import { Button, Input } from '../../components/radix'
import { WarehousePortalLink } from '../../components/engineer/WarehousePortalLink'
import { AtBranchCard } from '../../components/engineer/AtBranchCard'
import { RecentLocations } from '../../components/engineer/RecentLocations'
import { UseMyLocationButton } from '../../components/engineer/UseMyLocationButton'
import { useMe } from '../../auth/hooks/useMe'
import { useDebounce } from '../../hooks/useDebounce'
import { useBranchesNearPostcode } from '../../hooks/useBranchesNearPostcode'
import { useDeviceLocation } from '../../hooks/useDeviceLocation'
import { useCurrentAtBranches } from '../../hooks/useCurrentAtBranches'
import { useRecentLocations } from '../../hooks/useRecentLocations'
import { useSetEngineerPostcode } from '../../hooks/useSetEngineerPostcode'
import {
  POSTCODE_FAILURE_MESSAGES,
  searchPostcodes,
  lookupPostcode,
} from '../../services/postcodes'
import {
  resolveAtBranchesToSave,
  writeEngineerSession,
} from '../../services/engineerSession'
import type { AtBranchSource } from '../../services/engineerSession'
import { POST_POSTCODE_LANDING } from '../../config/features'

// Max time to hold the full-screen loader while restoring a saved location
// before falling through to the postcode form. Bounds the wait so a stalled
// auth-me or postcodes.io request can't trap a session-less engineer with no
// way to enter a postcode manually.
const RESTORE_MAX_WAIT_MS = 8000

export function JobSetup() {
  const navigate = useNavigate()
  const { tenantSlug } = useParams()
  const [searchParams] = useSearchParams()
  const [postcode, setPostcode] = useState('')
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Set to true when an auto-restore from the DB postcode fails its coordinate
  // lookup, so the redirect guard below stops holding the loader and lets the
  // user fall through to the postcode entry form.
  const [restoreFailed, setRestoreFailed] = useState(false)
  // Set to true when the restore wait exceeds RESTORE_MAX_WAIT_MS, so a stalled
  // auth-me / postcodes.io request falls through to the form instead of holding
  // the loader forever.
  const [restoreTimedOut, setRestoreTimedOut] = useState(false)
  // True while the postcode in the field came from the device rather than the
  // keyboard, so the "check it's right" hint shows. Cleared as soon as the
  // engineer edits it.
  const [isPostcodeFromDevice, setIsPostcodeFromDevice] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const suggestionsRef = useRef<HTMLDivElement>(null)

  // The postcode in use and the nearby branches pinned for it, so the
  // branch card opens on what's pinned when the field holds that postcode.
  const currentAtBranches = useCurrentAtBranches()

  // "Use my current location". Fills the field only — the engineer still
  // confirms with the primary button below.
  const {
    isSupported: isDeviceLocationSupported,
    status: deviceLocationStatus,
    failureReason: deviceLocationFailure,
    found: deviceLocationFound,
    atBranchIds,
    setAtBranchIds,
    locate: locateDevicePostcode,
    cancel: cancelDeviceLocation,
  } = useDeviceLocation(currentAtBranches)

  // Get user data to check for last known postcode
  const { data: meData, isLoading: isMeLoading } = useMe()

  // Shared flow that resolves + persists a new working postcode.
  const { setEngineerPostcode } = useSetEngineerPostcode()

  // Filtered against the postcode actually in use, not the field: chips must
  // not disappear as the engineer types.
  const { recentPostcodes } = useRecentLocations(meData?.last_postcode ?? '')

  // Check if user explicitly wants to change postcode (came from "Change Location" button)
  const wantsToChangePostcode = searchParams.get('change') === 'true'

  /**
   * Postcode Session Flow:
   *
   * Scenario 1: Fresh app load (no session, has last_postcode in DB)
   * - Auto-restore User.last_postcode from database
   * - Fetch coordinates from postcodes.io API
   * - Set sessionStorage and redirect to search
   *
   * Scenario 2: User clicks "Change Location" (empty cart)
   * - Clear sessionStorage in Search page
   * - Navigate here with ?change=true
   * - Skip auto-restore, let user enter new postcode
   *
   * Scenario 3: User clicks "Change Location" (cart has items)
   * - Show warning dialog
   * - On confirm: clear cart, clear sessionStorage
   * - Navigate here with ?change=true
   * - Skip auto-restore, let user enter new postcode
   *
   * Scenario 4: User navigates here directly (already has session)
   * - Redirect to search immediately (don't need postcode entry)
   */
  useEffect(() => {
    // If user explicitly wants to change postcode, don't auto-restore
    if (wantsToChangePostcode) {
      return
    }

    const savedSession = sessionStorage.getItem('engineerSession')

    if (savedSession) {
      // Session exists, redirect to home (DEV-46921 — Home is the
      // landing page once a postcode is set; before that JobSetup takes
      // priority per Noreen 16-07-26).
      navigate(`/${tenantSlug}/engineer/${POST_POSTCODE_LANDING}`)
    } else if (meData?.last_postcode) {
      // No session but user has last known postcode - restore it
      // Fetch coordinates from API and then redirect
      const restoreSession = async () => {
        const resolved = await lookupPostcode(meData.last_postcode!)

        if (resolved) {
          // Write the canonical spelling, not what is stored on the profile —
          // profiles saved before OCTO-550159 hold unspaced postcodes, and the
          // session feeds the order payload.
          writeEngineerSession(resolved.postcode, resolved)

          // Redirect to search with restored postcode
          navigate(`/${tenantSlug}/engineer/${POST_POSTCODE_LANDING}`)
        } else {
          // Failed to lookup coordinates - stay on page for user to enter postcode
          console.warn(`Failed to lookup coordinates for postcode: ${meData.last_postcode}`)
          setRestoreFailed(true)
        }
      }

      restoreSession()
    }
    // If neither exists, stay on this page for user to enter postcode
  }, [meData, navigate, tenantSlug, wantsToChangePostcode])

  // Bound the saved-location restore wait. If auth-me is still loading or the
  // postcodes.io lookup stalls, fall through to the postcode form after a max
  // wait so a session-less engineer is never trapped on the loader.
  useEffect(() => {
    if (wantsToChangePostcode) return
    const hasSession = sessionStorage.getItem('engineerSession') !== null
    const awaitingRestore =
      !hasSession &&
      !restoreFailed &&
      (isMeLoading || Boolean(meData?.last_postcode))
    if (!awaitingRestore) return

    const timer = setTimeout(
      () => setRestoreTimedOut(true),
      RESTORE_MAX_WAIT_MS,
    )
    return () => clearTimeout(timer)
  }, [wantsToChangePostcode, restoreFailed, isMeLoading, meData])

  // Debounce postcode input to prevent flickering during fast typing
  const debouncedPostcode = useDebounce(postcode, 300)

  // Names the postcode being searched, so the engineer confirms a value they
  // can see — it may have come from the device rather than the keyboard.
  // Matches the Change Location drawer's primary button.
  const trimmedPostcode = postcode.trim()
  const primaryLabel = isSubmitting
    ? 'Loading…'
    : trimmedPostcode
      ? `Search near ${trimmedPostcode}`
      : 'Search'

  // Postcode autocomplete query - uses debounced value to reduce API calls
  const { data: suggestions = [], isFetching } = useQuery({
    queryKey: ['postcode-autocomplete', debouncedPostcode],
    queryFn: () => searchPostcodes(debouncedPostcode),
    enabled: debouncedPostcode.length >= 2,
    staleTime: 30000,
    // Keep previous data while fetching new results to prevent flicker
    placeholderData: (previousData) => previousData,
  })

  // Close suggestions when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Counters near the typed postcode. Only consulted when the device route
  // has nothing on offer, so the two never compete for the card.
  const {
    branches: postcodeBranches,
    selectedBranchIds: postcodeAtBranchIds,
    setSelectedBranchIds: setPostcodeAtBranchIds,
  } = useBranchesNearPostcode(
    postcode,
    deviceLocationFound === null,
    currentAtBranches,
  )

  // The device knows where the engineer is; a typed postcode only knows the
  // area. Wording downstream depends on which, so the source travels with it.
  const atBranchSource: AtBranchSource = deviceLocationFound
    ? 'device'
    : 'postcode'
  const offeredBranches = deviceLocationFound
    ? deviceLocationFound.branches
    : postcodeBranches
  const offeredSelectionIds = deviceLocationFound
    ? atBranchIds
    : postcodeAtBranchIds
  const setOfferedSelectionIds = deviceLocationFound
    ? setAtBranchIds
    : setPostcodeAtBranchIds

  // The branches to carry through on confirm: the ticked ones the card
  // showed, plus — for the postcode already in use — any pin it didn't show,
  // so a card still loading or a failed lookup never wipes pins.
  const confirmedAtBranches = resolveAtBranchesToSave(
    postcode,
    offeredBranches,
    offeredSelectionIds,
    atBranchSource,
    currentAtBranches,
  )

  /**
   * Fill the field from a suggestion or a recent postcode. Nothing is applied:
   * like typing it, the nearby branches for it are offered and the engineer
   * confirms with the primary button.
   */
  const handleSelectPostcode = (selected: string) => {
    setPostcode(selected)
    setShowSuggestions(false)
    setError(null)
    // Choosing a different postcode means the device result no longer
    // describes the field, so its "check it's right" hint and any branch
    // card go with it; the typed-postcode lookup takes over.
    setIsPostcodeFromDevice(false)
    cancelDeviceLocation()
  }

  /**
   * Fill the postcode field from the device position. Nothing is applied: the
   * engineer checks the postcode and presses the primary button. A failure
   * leaves the field untouched and surfaces a message under the button.
   */
  const handleUseMyLocation = async () => {
    const foundPostcode = await locateDevicePostcode()
    if (!foundPostcode) {
      return
    }
    setPostcode(foundPostcode)
    setShowSuggestions(false)
    setError(null)
    setIsPostcodeFromDevice(true)
  }

  const handleStartSearching = async () => {
    if (!postcode.trim()) {
      setError('Please enter a postcode to continue')
      return
    }

    // Any error stays on screen while this check runs: clearing it here made
    // the message collapse and reappear on every press of the same wrong
    // postcode, moving everything below it (the LOC-03 bounce, as in the
    // drawer). It is cleared as soon as the engineer edits the field.
    setIsSubmitting(true)

    // Resolve + persist the postcode (backend, auth-me cache, tab session).
    const result = await setEngineerPostcode(postcode, confirmedAtBranches)

    if (!result.ok) {
      setError(POSTCODE_FAILURE_MESSAGES[result.failure ?? 'not_found'])
      setIsSubmitting(false)
      return
    }

    // Navigate to search page
    navigate(`/${tenantSlug}/engineer/${POST_POSTCODE_LANDING}`)
  }

  // AC1 (DEV-46921): if the user already has a saved location we redirect to
  // home/search from the effect above. That redirect runs *after* first paint,
  // so without a guard the postcode form flashes on screen (and stays visible
  // for the whole postcodes.io lookup in the DB-restore case) before we leave.
  // Hold a loader instead of the form while a redirect is pending. This is a
  // synchronous decision so nothing paints the form first. The form still
  // shows for: an explicit "Change Location" (?change=true), a genuine new
  // user (no session, no stored postcode), or a failed DB restore.
  const hasSavedSession =
    !wantsToChangePostcode && sessionStorage.getItem('engineerSession') !== null
  const isAwaitingRestore =
    !wantsToChangePostcode &&
    !restoreFailed &&
    !restoreTimedOut &&
    !hasSavedSession &&
    (isMeLoading || Boolean(meData?.last_postcode))
  const isRedirecting = hasSavedSession || isAwaitingRestore

  if (isRedirecting) {
    return (
      <EngineerLayout hideBottomNav hideHeader>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2
            className="w-8 h-8 animate-spin text-brand-primary"
            aria-hidden="true"
          />
          <span className="sr-only">Loading your saved location…</span>
        </div>
      </EngineerLayout>
    )
  }

  return (
    <EngineerLayout hideBottomNav hideHeader>
      <div className="min-h-screen flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          {/* Header */}
          <div className="text-center mb-8">
            {/* Icon tile background hardcoded to the mockup's neutral
             * light blue-white (#EEF6FF, sampled with ColorZilla from
             * /job-setup on the deployed mockup). Intentionally NOT
             * brand-derived: the mockup uses a fixed neutral so the icon
             * reads consistently across every tenant palette. The old
             * `bg-brand-primary-lightest` class collapsed to pure white
             * for bright primaries via lightenColor at 90%, and a
             * brand-tinted color-mix drifted from the mockup (produced a
             * pink circle on magenta-primary tenants). */}
            <div
              className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4"
              style={{ backgroundColor: '#EEF6FF' }}
            >
              <MapPin className="w-8 h-8 text-brand-primary" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Where are you working?
            </h1>
            <p className="text-gray-600">
              We'll show suppliers near here, and deliver here by default
            </p>
          </div>

          {/* Postcode Input Card */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
            <div className="space-y-4">
              <div ref={suggestionsRef} className="relative">
                <Input
                  ref={inputRef}
                  id="postcode"
                  label="Your postcode"
                  placeholder="e.g. M1 3DZ"
                  value={postcode}
                  onChange={(e) => {
                    setPostcode(e.target.value.toUpperCase())
                    setShowSuggestions(true)
                    setError(null)
                    // What the engineer types wins: drop a lookup still in
                    // flight so a late answer can't overwrite it, and stop
                    // calling the value "found from your location". `found`
                    // is covered too — the counter lookup runs on after the
                    // postcode lands, and its result must not reinstate a
                    // card for a postcode the engineer has moved away from.
                    // A failure message is left alone: it tells them to type,
                    // which is exactly what they're doing.
                    if (
                      isPostcodeFromDevice ||
                      deviceLocationStatus === 'locating' ||
                      deviceLocationStatus === 'found'
                    ) {
                      cancelDeviceLocation()
                      setIsPostcodeFromDevice(false)
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      setShowSuggestions(false)
                      handleStartSearching()
                    }
                  }}
                  onFocus={() => setShowSuggestions(true)}
                  leftIcon={<MapPin className="w-5 h-5" />}
                  rightIcon={isFetching ? <Loader2 className="w-5 h-5 animate-spin" /> : undefined}
                  error={error || undefined}
                  hint={
                    isPostcodeFromDevice
                      ? "Found from your location. Check it's right."
                      : undefined
                  }
                  // No reserved message line: with nothing to show it left a
                  // blank gap above "Use my current location". The line now
                  // only takes space when an error or hint is showing.
                  autoComplete="off"
                />

                {/* Floats below the field, anchored to its full height rather
                    than a fixed offset. The old fixed offset landed it over
                    the field's own error / hint line, and it sat above "Use my
                    current location" and the recent postcodes with no shadow
                    to explain why they had disappeared. */}
                {showSuggestions && suggestions.length > 0 && (
                  <div className="absolute left-0 right-0 top-full z-20 mt-1 max-h-64 overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-lg">
                    {suggestions.map((postcodeValue) => (
                      <button
                        key={postcodeValue}
                        type="button"
                        onClick={() => handleSelectPostcode(postcodeValue)}
                        className="w-full px-4 py-3 text-left text-base font-medium text-gray-900 hover:bg-gray-50 active:bg-gray-100 border-b border-gray-100 last:border-b-0"
                      >
                        {postcodeValue}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <UseMyLocationButton
                isSupported={isDeviceLocationSupported}
                status={deviceLocationStatus}
                failureReason={deviceLocationFailure}
                onLocate={handleUseMyLocation}
              />

              {/* AC3: editing the postcode drops the device result, which
                  takes this card with it. */}
              <AtBranchCard
                branches={offeredBranches}
                source={atBranchSource}
                selectedBranchIds={offeredSelectionIds}
                onChange={setOfferedSelectionIds}
              />

              {/* Tapping a recent postcode fills the field, exactly as picking
                  a suggestion does — same handler, so both stay in step. The
                  engineer then sees its nearby branches and confirms.
                  Replaces DEV-51044 AC6's "applies it at once". */}
              <RecentLocations
                postcodes={recentPostcodes}
                onSelect={handleSelectPostcode}
                disabled={isSubmitting}
              />
            </div>
          </div>

          {/* Start Button */}
          <Button
            onClick={() => handleStartSearching()}
            disabled={isSubmitting}
            size="lg"
            className="w-full mt-6"
          >
            {isSubmitting ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Search className="w-5 h-5" />
            )}
            {primaryLabel}
          </Button>

          {/* My Orders Link — brand-primary at rest to match the mockup
           * (the mockup styles this link as an accent, not muted gray). */}
          <button
            onClick={() => navigate(`/${tenantSlug}/engineer/orders`)}
            className="w-full mt-4 flex items-center justify-center gap-2 text-sm font-medium text-brand-primary hover:text-brand-primary-hover transition-colors py-2"
          >
            <ClipboardList className="w-4 h-4" />
            View my orders
          </button>

          {/* DEV-49100 — low-emphasis warehouse portal entry point,
           * below the divider outside the postcode card. */}
          <div className="mt-5 pt-4 border-t border-gray-200 flex justify-center">
            <WarehousePortalLink variant="inline" />
          </div>

          {/* Helper text */}
          <p className="text-center text-xs text-gray-400 mt-2">
            Your location will be saved and synced across all your devices
          </p>
        </div>
      </div>
    </EngineerLayout>
  )
}
